import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LegalConsentComponent } from '../../../legal/legal-consent/legal-consent.component';
import { Router, RouterModule } from '@angular/router';
import { RegistrationService } from '../../../../services/api/registration';
import { LanguageService } from '../../../../services/i18n/language';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-public-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, LegalConsentComponent],
  templateUrl: './public-registration.html',
  styleUrl: './public-registration.scss'
})
export class PublicRegistrationComponent implements OnInit, OnDestroy {
  registrationForm: FormGroup;
  loading = false;
  error: string | null = null;
  success = false;

  // Переводы
  titleText = 'Регистрация';
  subtitleText = 'Заполните форму для создания аккаунта';
  personalInfoText = 'Личная информация';
  passwordText = 'Пароль';
  emailLabel = 'Email';
  fullNameLabel = 'Полное имя';
  phoneLabel = 'Телефон';
  passwordLabel = 'Пароль';
  confirmPasswordLabel = 'Подтвердите пароль';
  submitText = 'Зарегистрироваться';
  submittingText = 'Регистрация...';
  hasAccountText = 'Уже есть аккаунт? Войти';
  successText = 'Регистрация успешно завершена! Вы будете перенаправлены на страницу входа...';
  requiredText = '*';
  fullNamePlaceholderText = 'Имя Фамилия';
  passwordPlaceholderText = 'Минимум 6 символов';
  confirmPasswordPlaceholderText = 'Повторите пароль';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private registrationService: RegistrationService,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      confirmPassword: ['', [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptPersonalData: [false, [Validators.requiredTrue]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.updateTranslations();

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('registration.public.title') || 'Регистрация';
    this.subtitleText = this.languageService.translate('registration.public.subtitle') || 'Заполните форму для создания аккаунта';
    this.personalInfoText = this.languageService.translate('registration.public.personalInfo') || 'Личная информация';
    this.passwordText = this.languageService.translate('registration.public.password') || 'Пароль';
    this.emailLabel = this.languageService.translate('registration.public.email') || 'Email';
    this.fullNameLabel = this.languageService.translate('registration.public.fullName') || 'Полное имя';
    this.phoneLabel = this.languageService.translate('registration.public.phone') || 'Телефон';
    this.passwordLabel = this.languageService.translate('registration.public.passwordField') || 'Пароль';
    this.confirmPasswordLabel = this.languageService.translate('registration.public.confirmPassword') || 'Подтвердите пароль';
    this.submitText = this.languageService.translate('registration.public.submit') || 'Зарегистрироваться';
    this.submittingText = this.languageService.translate('registration.public.submitting') || 'Регистрация...';
    this.hasAccountText = this.languageService.translate('registration.public.hasAccount') || 'Уже есть аккаунт? Войти';
    this.successText = this.languageService.translate('registration.public.success') || 'Регистрация успешно завершена! Вы будете перенаправлены на страницу входа...';
    this.requiredText = this.languageService.translate('common.required') || '*';
    this.fullNamePlaceholderText = this.languageService.translate('registration.cargoOwner.fullNamePlaceholder') || this.fullNamePlaceholderText;
    this.passwordPlaceholderText = this.languageService.translate('registration.cargoOwner.passwordPlaceholder') || this.passwordPlaceholderText;
    this.confirmPasswordPlaceholderText = this.languageService.translate('registration.cargoOwner.confirmPasswordPlaceholder') || this.confirmPasswordPlaceholderText;
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (confirmPassword && confirmPassword.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }
    
    return null;
  }

  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.registrationForm.value;
    const request = {
      email: formValue.email,
      fullName: formValue.fullName,
      phone: formValue.phone || undefined,
      password: formValue.password,
      roleId: 5 // ROLE_PUBLIC
    };

    this.registrationService.register(request).subscribe({
      next: () => {
        this.success = true;
        this.loading = false;
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      error: (err) => {
        this.loading = false;
        if (err.error && err.error.message) {
          this.error = err.error.message;
        } else if (err.error && typeof err.error === 'string') {
          this.error = err.error;
        } else {
          this.error = 'Ошибка при регистрации. Попробуйте еще раз.';
        }
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.registrationForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) {
        return 'Это поле обязательно для заполнения';
      }
      if (field.errors['email']) {
        return 'Введите корректный email';
      }
      if (field.errors['minlength']) {
        return `Минимальная длина: ${field.errors['minlength'].requiredLength} символов`;
      }
      if (field.errors['maxlength']) {
        return `Максимальная длина: ${field.errors['maxlength'].requiredLength} символов`;
      }
      if (field.errors['passwordMismatch']) {
        return 'Пароли не совпадают';
      }
    }
    return '';
  }
}
